import type { AccountResponse } from './types'

const referenceHeader = 'X-Signup-Error-Reference'
export type CredentialTranslator = (key: string, ...values: Array<string | number>) => string

export async function saveAccountCredentials(
  signupId: string,
  flowId: string,
  emailAddress: string,
  password: string,
  confirmPassword: string,
  t: CredentialTranslator,
): Promise<AccountResponse> {
  return requestCredentials(signupId, '', {
    method: 'POST',
    body: JSON.stringify({ flowId, emailAddress, password, confirmPassword }),
  }, t)
}

export function loadSavedAccountCredentials(signupId: string, flowId: string, t: CredentialTranslator): Promise<AccountResponse> {
  return requestCredentials(signupId, `?${new URLSearchParams({ flowId })}`, { method: 'GET' }, t)
}

async function requestCredentials(signupId: string, query: string, options: RequestInit, t: CredentialTranslator): Promise<AccountResponse> {
  // getRandomValues also works on local HTTP test devices without randomUUID.
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6] & 15) | 64
  bytes[8] = (bytes[8] & 63) | 128
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
  const reference = `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), 20_000)
  try {
    const response = await fetch(`/modern/${encodeURIComponent(signupId)}/account/credentials${query}`, {
      ...options,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        [referenceHeader]: reference,
      },
      cache: 'no-store',
      signal: controller.signal,
    })
    const body = await response.json().catch(() => null) as AccountResponse | null
    if (body && typeof body.status === 'string' && typeof body.message === 'string'
        && body.fieldErrors && typeof body.fieldErrors === 'object'
        && !Array.isArray(body.fieldErrors)
        && (body.status !== 'SUCCESS' || (response.ok && typeof body.emailAddress === 'string' && body.emailAddress.length > 0))) {
      if (body.status === 'PURCHASE_REQUIRED' || body.status === 'SESSION_MISMATCH') {
        return { ...body, message: t('signup.account.error.session') }
      }
      return body
    }
    const serverReference = response.headers.get(referenceHeader)
    const safeReference = serverReference && /^[a-f\d-]{36}$/i.test(serverReference)
      ? serverReference : reference
    const messageKey = response.status === 401 || response.status === 403 || response.status === 405
      ? 'signup.account.error.blocked'
      : 'signup.account.error.unexpectedResponse'
    throw new Error(t(messageKey, safeReference, response.status))
  }
  catch (error) {
    if (controller.signal.aborted || error instanceof TypeError) {
      throw new Error(t('signup.account.error.connection', reference))
    }
    throw error
  }
  finally {
    clearTimeout(timeout)
  }
}
